<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import AiDraftsApi from 'dashboard/api/inbox/aiDrafts';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

const props = defineProps({
  conversationId: {
    type: [String, Number],
    required: true,
  },
});

const draft = ref(null);
const pollTimer = ref(null);
const applied = ref(false);
// Purely client-side dismiss: hide the banner without recording any feedback.
// The draft row stays in DB and would reappear on reload unless superseded.
const dismissed = ref(false);

const isPending = computed(() => draft.value?.status === 'pending');
const isReady = computed(() => draft.value?.status === 'ready');
const isFailed = computed(() => draft.value?.status === 'failed');
const isVisible = computed(
  () => !dismissed.value && draft.value && draft.value.status !== 'consumed'
);

// Local-only close — don't record anything, just hide for this session.
const onClose = () => {
  dismissed.value = true;
};

const fetchLatest = async () => {
  if (!props.conversationId) return;
  try {
    const { data } = await AiDraftsApi.getLatest(props.conversationId);
    const prev = draft.value;
    draft.value = data.data;
    // Auto-apply to editor when a draft newly becomes ready
    if (
      draft.value?.status === 'ready' &&
      draft.value.content &&
      (!prev || prev.status !== 'ready' || prev.id !== draft.value.id)
    ) {
      applyToEditor(draft.value.content);
      applied.value = true;
    }
    publishDraftState();
  } catch (e) {
    draft.value = null;
    publishDraftState();
  }
};

// Broadcast current draft state so the reply toolbar (ReplyTopPanel) can
// enable the feedback button when a draft is available. Intentionally a
// window event — keeps wiring loose across sibling component trees.
const publishDraftState = () => {
  window.dispatchEvent(
    new CustomEvent('wybot:draft-loaded', {
      detail: {
        conversationId: props.conversationId,
        draftId: draft.value?.id || null,
        status: draft.value?.status || null,
        // Whether Stage-1 classifier ran successfully — gates the
        // 局势分析 button in ReplyTopPanel.
        hasClassifier: draft.value?.has_classifier || false,
      },
    })
  );
};

const applyToEditor = content => {
  window.dispatchEvent(
    new CustomEvent('wybot:apply-ai-draft', {
      detail: { content, draftId: draft.value?.id },
    })
  );
};

const startPolling = () => {
  stopPolling();
  pollTimer.value = setInterval(() => {
    if (isPending.value) fetchLatest();
    else stopPolling();
  }, 2000);
};

const stopPolling = () => {
  if (pollTimer.value) {
    clearInterval(pollTimer.value);
    pollTimer.value = null;
  }
};

// When the user sends a message via the normal ReplyBox, mark the draft as
// accepted (content matches original) or edited (content was modified).
const onMessageSent = async payload => {
  if (!draft.value || !isReady.value) return;
  const sent = payload?.content || '';
  const original = (draft.value.content || '').trim();
  const isExact = sent.trim() === original;
  try {
    await AiDraftsApi.markOutcome(props.conversationId, draft.value.id, {
      actionType: isExact ? 'accepted' : 'edited',
      finalContent: sent,
      sentMessageId: payload?.messageId,
    });
  } catch (e) {
    // swallow
  }
  draft.value = null;
};

const onRegenerated = e => {
  // eslint-disable-next-line no-console
  console.info('[AiDraftBanner] regenerated event', {
    detailConversationId: e?.detail?.conversationId,
    thisConversationId: props.conversationId,
    draftStatus: e?.detail?.draft?.status,
  });
  // Ignore events for other conversations.
  // Loose comparison because IDs may cross string/number boundaries.
  if (String(e?.detail?.conversationId) !== String(props.conversationId)) {
    // eslint-disable-next-line no-console
    console.warn('[AiDraftBanner] ignored: conversation id mismatch');
    return;
  }

  // Adopt the new pending draft immediately so the user sees the indicator
  // without waiting for the next poll cycle.
  if (e?.detail?.draft) {
    draft.value = e.detail.draft;
    applied.value = false;
  }
  // New generation: re-open the banner if the user had dismissed it.
  dismissed.value = false;

  // Regardless of whether the event had a payload, force a fetch and start
  // polling — the new draft may still be pending, and polling stops
  // automatically once it transitions to ready / failed.
  fetchLatest();
  startPolling();
};

onMounted(() => {
  fetchLatest();
  emitter.on(BUS_EVENTS.MESSAGE_SENT, onMessageSent);
  window.addEventListener('wybot:draft-regenerated', onRegenerated);
});

onBeforeUnmount(() => {
  stopPolling();
  emitter.off(BUS_EVENTS.MESSAGE_SENT, onMessageSent);
  window.removeEventListener('wybot:draft-regenerated', onRegenerated);
});

watch(
  () => props.conversationId,
  () => {
    stopPolling();
    applied.value = false;
    dismissed.value = false; // switching conversations resets the local dismiss
    fetchLatest();
  }
);

watch(
  isPending,
  val => {
    if (val) startPolling();
    else stopPolling();
  },
  { immediate: true }
);

defineExpose({ refresh: fetchLatest });
</script>

<template>
  <div
    v-if="isVisible"
    class="ai-draft-banner mx-4 mt-2 px-3 py-2 rounded border text-xs flex items-center gap-2"
    :class="{
      'bg-woot-25 border-woot-200 text-woot-700': isReady,
      'bg-slate-50 border-slate-200 text-slate-600': isPending,
      'bg-red-50 border-red-200 text-red-700': isFailed,
    }"
  >
    <span v-if="isPending" class="animate-pulse">✨</span>
    <span v-else-if="isReady">✨</span>
    <span v-else-if="isFailed">❌</span>

    <span class="flex-1 truncate">
      <template v-if="isPending">
        AI 正在起草回复... 可等待或直接手动输入
      </template>
      <template v-else-if="isReady">
        AI 建议已填入回复框，编辑后发送即可。
        <span
          v-if="draft.kb_references && draft.kb_references.length"
          class="text-slate-500"
        >
          · 引用 {{ draft.kb_references.length }} 条知识库
        </span>
      </template>
      <template v-else-if="isFailed">
        生成失败: {{ (draft.error_message || '未知错误').slice(0, 80) }} ·
        点顶部 ✨ 建议按钮可重新生成
      </template>
    </span>
    <button
      class="inline-flex items-center justify-center w-5 h-5 text-slate-400 hover:text-slate-600 hover:bg-white/60 rounded-full"
      title="关闭提示"
      @click="onClose"
    >
      ✕
    </button>
  </div>
</template>
